import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Icon from "components/AppIcon";
import ClabLogoPng from "components/ui/ClabLogoPng";
import { authAPI } from "utils/api";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Input, 2: Success
  const [email, setEmail] = useState("");
  const [userType, setUserType] = useState("clab"); // 'clab', 'employer', 'recruiter'
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      await authAPI.requestPasswordReset({ email, userType });
      setStep(2);
    } catch (err) {
      console.error(err);
      // For security reasons, we might still want to show success or a generic message.
      // But typically we show the error if it's strictly a dev/internal tool, 
      // or generic "If account exists..." for public.
      // Given the previous mockup showed "If an account exists...", we can probably just proceed to step 2 
      // OR show the error if it's a connection error.
      
      // Let's show the error if it's explicitly strictly an error we want the user to know (like invalid email format),
      // otherwise, for "User not found", it's safer to pretend success (Security Best Practice).
      // However, usually users prefer to know if they typed it wrong.
      // I'll leave the success screen as the default behavior even on error (safest), but log the error.
      // EXCEPT if it's a network error.
      
      // Actually, let's just show the error message on screen for now for better UX during development.
      setError(err.response?.data?.message || "Failed to send reset link. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center mb-6">
           <ClabLogoPng size="xl" src="/assets/clab_logo.png" />
        </div>
        <h2 className="mt-2 text-center text-3xl font-extrabold text-gray-900">
          Reset your password
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Enter your details to receive reset instructions
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 text-sm rounded border border-red-200">
               {error}
            </div>
          )}
          {step === 1 ? (
            <form className="space-y-6" onSubmit={handleSubmit}>
              {/* User Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select User Type
                </label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => setUserType("clab")}
                    className={`flex items-center justify-center px-3 py-2 border text-sm font-medium rounded-md focus:outline-none ${
                      userType === "clab"
                        ? "bg-primary-50 border-primary-500 text-primary-700 ring-1 ring-primary-500"
                        : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    CLAB User
                  </button>
                  <button
                    type="button"
                    onClick={() => setUserType("employer")}
                    className={`flex items-center justify-center px-3 py-2 border text-sm font-medium rounded-md focus:outline-none ${
                      userType === "employer"
                        ? "bg-primary-50 border-primary-500 text-primary-700 ring-1 ring-primary-500"
                        : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    Employer
                  </button>
                  <button
                    type="button"
                    onClick={() => setUserType("recruiter")}
                    className={`flex items-center justify-center px-3 py-2 border text-sm font-medium rounded-md focus:outline-none ${
                      userType === "recruiter"
                        ? "bg-primary-50 border-primary-500 text-primary-700 ring-1 ring-primary-500"
                        : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    Recruiter
                  </button>
                </div>
              </div>

              {/* Email Input */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Icon name="Mail" size={20} className="text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="focus:ring-primary-500 focus:border-primary-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md py-3"
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  {isLoading ? (
                     <span className="flex items-center">
                        <Icon name="Loader" size={16} className="animate-spin mr-2" />
                        Sending...
                     </span>
                  ) : (
                    "Send Reset Link"
                  )}
                </button>
              </div>
            </form>
          ) : (
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <Icon name="Check" size={24} className="text-green-600" />
              </div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Check your email
              </h3>
              <div className="mt-2 text-sm text-gray-500">
                <p>
                  If an account exists for <strong>{email}</strong> ({userType}), we have sent instructions to reset your password.
                </p>
                <p className="mt-2">
                   Please check your inbox and spam folder.
                </p>
              </div>
              <div className="mt-6">
                 <button
                   onClick={() => setStep(1)}
                   className="text-sm font-medium text-primary hover:text-primary-500"
                 >
                   Try another email
                 </button>
              </div>
            </div>
          )}

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  Or return to login
                </span>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-3">
               <Link
                to="/login-screen"
                className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
              >
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
